import { useCallback, useEffect, useReducer, useRef, useState } from "preact/hooks";
import { ChatApi, ChatApiError, type RemoteConversation } from "./api.ts";
import { chatReducer, initialChatState } from "./state.ts";

/** Cause d'une erreur, traduite par le panneau du chat. */
export type ChatErrorReason = "expired" | "unavailable" | "unreachable" | "busy" | "failed";

export type ChatStatus =
  | { kind: "connecting" }
  | { kind: "ready" }
  | { kind: "error"; reason: ChatErrorReason; fatal: boolean };

/** Délais de reconnexion du flux, en millisecondes. */
export const RECONNECT_DELAYS = [1000, 2000, 5000, 10000];

export interface ChatSession {
  api: ChatApi;
  productId: string;
  /** La conversation à ouvrir : celle du livre par défaut, ou un dialogue avec une de ses personnes. */
  open?: (api: ChatApi) => Promise<RemoteConversation>;
  /** Change quand `open` vise une autre conversation : le panneau repart de zéro. */
  key?: string;
}

export function useChat(session: ChatSession) {
  const { api, productId, open, key = "book" } = session;
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [status, setStatus] = useState<ChatStatus>({ kind: "connecting" });
  const conversationId = useRef<string>();
  // La conversation que `status` décrit : une question venue du livre attend celle du livre, pas un dialogue.
  const [readyKey, setReadyKey] = useState<string>();
  // La langue de la conversation ouverte, gardée par le serveur à chaque question (aucune pour celles commencées avant).
  const [language, setLanguage] = useState<string | null>(null);

  useEffect(() => {
    const abort = new AbortController();
    let attempt = 0;
    conversationId.current = undefined;
    setLanguage(null);
    dispatch({ type: "reset" });
    setStatus({ kind: "connecting" });
    setReadyKey(undefined);

    const fail = (e: unknown) => {
      if (e instanceof ChatApiError && e.isAuthError) {
        setStatus({ kind: "error", fatal: true, reason: "expired" });
        return true;
      }
      if (e instanceof ChatApiError && e.status === 404) {
        setStatus({ kind: "error", fatal: true, reason: "unavailable" });
        return true;
      }
      return false;
    };

    const loadMessages = async (id: string) => {
      dispatch({ type: "messagesLoaded", messages: await api.getMessages(id) });
    };

    (async () => {
      try {
        const conversation = await (open ? open(api) : api.findOrCreateConversation(productId));
        conversationId.current = conversation.id;
        setLanguage(conversation.language ?? null);
        await loadMessages(conversation.id);
        if (abort.signal.aborted) return;
        setStatus({ kind: "ready" });
        setReadyKey(key);
      } catch (e) {
        if (!fail(e)) setStatus({ kind: "error", fatal: true, reason: "unreachable" });
        return;
      }

      // Flux des réponses, reconnecté tant que le panneau est ouvert.
      while (!abort.signal.aborted) {
        const id = conversationId.current!;
        try {
          await api.stream(id, abort.signal, (event) => {
            if (event.type === "connected") {
              attempt = 0;
              setStatus({ kind: "ready" });
              void loadMessages(id).catch(() => {});
            }
            dispatch({ type: "sse", event });
          });
        } catch (e) {
          if (abort.signal.aborted) return;
          if (fail(e)) return;
        }
        if (abort.signal.aborted) return;
        dispatch({ type: "disconnected" });
        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)]!;
        attempt++;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    })();

    return () => abort.abort();
  }, [api, productId, key]);

  const send = useCallback(
    async (question: string, chosen?: string | null) => {
      const id = conversationId.current;
      const text = question.trim();
      if (!id || !text) return;
      setStatus((s) => (s.kind === "error" && !s.fatal ? { kind: "ready" } : s));
      dispatch({ type: "questionSent", question: text });
      try {
        // La réponse arrive par le flux ; la requête se termine quand elle est enregistrée.
        if (chosen) setLanguage(chosen);
        await api.sendMessage(id, text, chosen);
      } catch (e) {
        dispatch({ type: "questionFailed" });
        if (e instanceof ChatApiError && e.status === 409) {
          setStatus({ kind: "error", fatal: false, reason: "busy" });
        } else if (e instanceof ChatApiError && e.isAuthError) {
          setStatus({ kind: "error", fatal: true, reason: "expired" });
        } else {
          setStatus({ kind: "error", fatal: false, reason: "failed" });
        }
      }
    },
    [api],
  );

  const rate = useCallback(
    /** Vrai si la note est enregistrée ; `comment` accompagne une réponse jugée pas utile. */
    async (messageId: string, isGood: boolean | null, comment: string | null = null): Promise<boolean> => {
      try {
        dispatch({ type: "messageUpdated", message: await api.rateMessage(messageId, isGood, comment) });
        return true;
      } catch {
        // Note non enregistrée : sans conséquence pour la lecture.
        return false;
      }
    },
    [api],
  );

  return { state, status, readyKey, send, rate, language };
}
